import { useState, useRef, useEffect } from "react";
import type { Todo } from "@/App";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { GripVertical, Trash2, Pencil, Check, X } from "lucide-react";

interface TodoItemProps {
	todo: Todo;
	onToggle: (id: string) => void;
	onDelete: (id: string) => void;
	onEdit: (id: string, text: string) => void;
	isDragOverlay?: boolean;
	dragHandleProps?: Record<string, unknown>;
}

export function TodoItem({
	todo,
	onToggle,
	onDelete,
	onEdit,
	isDragOverlay,
	dragHandleProps,
}: TodoItemProps) {
	const [editing, setEditing] = useState(false);
	const [editText, setEditText] = useState(todo.text);
	const editRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (editing) {
			editRef.current?.focus();
			editRef.current?.select();
		}
	}, [editing]);

	const submitEdit = () => {
		const trimmed = editText.trim();
		if (trimmed && trimmed !== todo.text) {
			onEdit(todo.id, trimmed);
		}
		setEditing(false);
		setEditText(todo.text);
	};

	const cancelEdit = () => {
		setEditing(false);
		setEditText(todo.text);
	};

	return (
		<div
			className={`group flex items-center gap-2 rounded-lg border bg-card px-3 py-2.5 transition-colors hover:bg-accent/50 ${
				isDragOverlay ? "shadow-lg ring-2 ring-primary/20" : ""
			} ${todo.completed ? "opacity-60" : ""}`}
		>
			<button
				className="cursor-grab touch-none text-muted-foreground/40 hover:text-muted-foreground transition-colors"
				{...dragHandleProps}
				tabIndex={-1}
			>
				<GripVertical className="h-4 w-4" />
			</button>

			<Checkbox
				checked={todo.completed}
				onCheckedChange={() => onToggle(todo.id)}
				className="shrink-0"
			/>

			{editing ? (
				<form
					onSubmit={(e) => {
						e.preventDefault();
						submitEdit();
					}}
					className="flex flex-1 items-center gap-1"
				>
					<input
						ref={editRef}
						value={editText}
						onChange={(e) => setEditText(e.target.value)}
						onKeyDown={(e) => e.key === "Escape" && cancelEdit()}
						onBlur={submitEdit}
						className="flex-1 bg-transparent outline-none text-sm"
					/>
					<Button
						type="submit"
						variant="ghost"
						size="icon"
						className="h-6 w-6"
					>
						<Check className="h-3 w-3" />
					</Button>
					<Button
						type="button"
						variant="ghost"
						size="icon"
						className="h-6 w-6"
						onMouseDown={(e) => {
							e.preventDefault();
							cancelEdit();
						}}
					>
						<X className="h-3 w-3" />
					</Button>
				</form>
			) : (
				<>
					<span
						className={`flex-1 text-sm select-none ${
							todo.completed ? "line-through text-muted-foreground" : ""
						}`}
						onDoubleClick={() => {
							setEditText(todo.text);
							setEditing(true);
						}}
					>
						{todo.text}
					</span>
					<div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7 text-muted-foreground hover:text-foreground"
							onClick={() => {
								setEditText(todo.text);
								setEditing(true);
							}}
						>
							<Pencil className="h-3 w-3" />
						</Button>
						<Button
							variant="ghost"
							size="icon"
							className="h-7 w-7 text-muted-foreground hover:text-destructive"
							onClick={() => onDelete(todo.id)}
						>
							<Trash2 className="h-3 w-3" />
						</Button>
					</div>
				</>
			)}
		</div>
	);
}
